import { useState, useEffect, useCallback } from "react";
import { useMutation } from "@apollo/client";
import { createStatefulContext } from "../../../contexts/createStatefulContext";
import { useSalesforcePageContext } from "./useSalesforcePageContext";
import { useLatestSyncResult } from "../hooks/useLatestSyncResult";
import { useIntegrationSynchronization } from "../hooks/useIntegrationSynchronization";
import { useIntegrationSelections } from "../hooks/useIntegrationSelections";
import { useGenerateSyncErrorsReport } from "../hooks/useGenerateSyncErrorsReport";
import { useSnackbar } from "../../../components/Snackbar";
import {
  EnrichmentSynchronizationState,
  SFDCIntegration,
  SFDCIntegrationSyncSettings,
} from "../types";
import { DETAILS_PAGE_TEXTS, UNAVAILABLE_SYNC_STATES } from "../consts";
import { ENRICHMENT_SYNCHRONIZATION_MUTATION } from "../../../apis/salesforce/queries";
import { HG_FORCE_CLIENT } from "../../../utils/consts";

interface SalesforceIntegrationDetailsContextProps {
  integration: SFDCIntegration;
}

export const {
  WithSalesforceIntegrationDetailsContext,
  useSalesforceIntegrationDetailsContext,
} = createStatefulContext(
  "SalesforceIntegrationDetails",
  ({ integration }: SalesforceIntegrationDetailsContextProps) => {
    const integrationId = integration.id;

    // State management for banners
    const [showReauthenticationBanner, setShowReauthenticationBanner] =
      useState<boolean>(false);
    const [showReminderBanner, setShowReminderBanner] = useState<boolean>(true);

    // Settings modal state
    const [isSettingsModalOpen, setIsSettingsModalOpen] =
      useState<boolean>(false);

    // Draft settings state (shared between view and actions)
    const [draftSettings, setDraftSettings] = useState<
      SFDCIntegrationSyncSettings | undefined
    >(integration.syncSettings);

    // State management for polling
    const [shouldPoll, setShouldPoll] = useState<boolean>(false);

    // State to track if we're initiating sync (bridge the gap until synchronizationState updates)
    const [isInitiatingSync, setIsInitiatingSync] = useState<boolean>(false);

    // Integration selections (products and spend categories)
    const { products: productsSelection, categories: categoriesSelection } =
      useIntegrationSelections(integrationId);

    // Snackbar notifications
    const { showError, showInfo } = useSnackbar();

    // Get integrations from SalesforcePageContext
    const {
      orgs: integrations,
      loading: integrationsLoading,
      error: integrationsError,
    } = useSalesforcePageContext();

    const {
      syncResult,
      loading: syncResultLoading,
      refetch: refetchSyncResult,
    } = useLatestSyncResult(integrationId);
    const {
      synchronizationState,
      loading: synchronizationLoading,
      refetch: refetchSynchronization,
      startPolling: startSyncPolling,
      stopPolling: stopSyncPolling,
    } = useIntegrationSynchronization(integrationId);
    const { generateSyncErrorsReport, loading: generatingReport } =
      useGenerateSyncErrorsReport();

    // Sync mutation
    const [startSynchronizationMutation] = useMutation(
      ENRICHMENT_SYNCHRONIZATION_MUTATION,
      { context: { clientName: HG_FORCE_CLIENT } }
    );

    // Keep draft settings in sync when integration changes
    useEffect(() => {
      setDraftSettings(integration.syncSettings);
    }, [integration.id, integration.syncSettings]);

    // Helper function to update draft settings
    const updateDraftSetting = useCallback(
      <K extends keyof SFDCIntegrationSyncSettings>(
        key: K,
        value: SFDCIntegrationSyncSettings[K]
      ) => {
        setDraftSettings((prev: SFDCIntegrationSyncSettings | undefined) =>
          prev ? { ...prev, [key]: value } : undefined
        );
      },
      []
    );

    // Update reauthentication banner visibility based on sync state
    useEffect(() => {
      setShowReauthenticationBanner(
        synchronizationState ===
          EnrichmentSynchronizationState.INTEGRATION_UNHEALTHY
      );
    }, [synchronizationState]);

    // Show error toast when sync fails due to missing products/categories
    useEffect(() => {
      if (
        synchronizationState ===
        EnrichmentSynchronizationState.CHOOSE_SPEND_OR_PRODUCT
      ) {
        showError(DETAILS_PAGE_TEXTS.sync.noProductOrSpendError);
      }
    }, [synchronizationState, showError]);

    // Manage polling lifecycle and trigger sync result refetch
    useEffect(() => {
      if (
        synchronizationState &&
        UNAVAILABLE_SYNC_STATES.includes(synchronizationState)
      ) {
        // Start polling when sync begins
        setShouldPoll(true);
        startSyncPolling(5000);
      } else if (shouldPoll) {
        // Sync completed or errored, stop polling and refetch results
        setShouldPoll(false);
        stopSyncPolling();
        refetchSyncResult();

        // Show message to give user feedback on sync completion
        if (synchronizationState === EnrichmentSynchronizationState.AVAILABLE) {
          showInfo(DETAILS_PAGE_TEXTS.sync.completed);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      synchronizationState,
      shouldPoll,
      refetchSyncResult,
      startSyncPolling,
      stopSyncPolling,
    ]);

    // Handle sync function
    const handleSync = useCallback(async () => {
      try {
        setIsInitiatingSync(true);

        await startSynchronizationMutation({ variables: { integrationId } });

        // Wait for 1 second before refetching synchronization state because it's not updated immediately
        setTimeout(async () => {
          await refetchSynchronization();
          setIsInitiatingSync(false);
        }, 1000);
      } catch (error) {
        setIsInitiatingSync(false);
        console.error("Sync failed:", error);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [integrationId, startSynchronizationMutation]);

    // Handle product selection
    const handleProductsSelect = async (productData: {
      ids: number[];
      inclusionMethod: string;
    }) => {
      const result = await productsSelection.handleUpdate(productData);
      if (result.success) {
        setShowReminderBanner(true);
      } else {
        // Show specific error message based on error type
        if (result.error === "product_quota_exceeded") {
          showError(DETAILS_PAGE_TEXTS.productSelection.quotaExceededError);
        } else {
          showError(DETAILS_PAGE_TEXTS.productSelection.saveError);
        }
      }
    };

    // Handle spend category selection
    const handleSpendCategoriesSelect = async (categoryIds: string[]) => {
      const result = await categoriesSelection.handleUpdate(categoryIds);
      if (result.success) {
        setShowReminderBanner(true);
      } else {
        showError(DETAILS_PAGE_TEXTS.spendCategories.saveError);
      }
    };

    // Handle generate sync errors report
    const handleGenerateSyncErrorsReport = async () => {
      if (!integrationId) return;
      try {
        await generateSyncErrorsReport(integrationId);
        showInfo(DETAILS_PAGE_TEXTS.syncErrorsReport.success);
      } catch (error) {
        showError(
          DETAILS_PAGE_TEXTS.syncErrorsReport.error(
            error instanceof Error ? error.message : "Unknown error"
          )
        );
      }
    };

    return {
      // Core data
      integrationId,
      integration,
      integrations,
      integrationsLoading,
      integrationsError,

      // Sync related
      synchronizationState,
      synchronizationLoading,
      syncResult,
      syncResultLoading,
      handleGenerateSyncErrorsReport,
      handleSync,
      isInitiatingSync,
      refetchSynchronization,

      // Selection management
      productsSelection,
      categoriesSelection,
      handleProductsSelect,
      handleSpendCategoriesSelect,

      // UI state
      showReauthenticationBanner,
      setShowReauthenticationBanner,
      showReminderBanner,
      setShowReminderBanner,
      generatingReport,

      // Settings modal
      isSettingsModalOpen,
      setIsSettingsModalOpen,

      // Draft settings state
      draftSettings,
      setDraftSettings,
      updateDraftSetting,
    };
  }
);
